/**
 * Document fingerprinting & duplicate detection
 * ---------------------------------------------
 * Two complementary signals, computed at upload and compared in Process Docs:
 *
 *   1. file_hash       SHA-256 of the stored file bytes. Equal -> byte-identical
 *                      file -> 100% duplicate (a literal re-upload).
 *   2. content_minhash A MinHash signature over the document's normalized text +
 *                      extracted Filing fields. Catches the SAME document re-saved
 *                      / re-scanned / JPG-vs-PDF / with OCR noise, where the bytes
 *                      differ but the content is the same. Yields a similarity %.
 *
 * Nothing here blocks an upload — duplicates are flagged AFTER they land, in the
 * document list, with a match percentage.
 */

// How many MinHash permutations. More = more accurate similarity estimate, longer
// signature. 64 keeps the serialized string small (~a few hundred chars).
export const MINHASH_SIZE = 64;

// At or above this match % two documents are considered duplicates of each other.
export const DUP_THRESHOLD = 80;

// ── byte hash ────────────────────────────────────────────────────────────────

/** SHA-256 of raw bytes as a lowercase hex string (Web Crypto). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  // crypto.subtle wants an ArrayBuffer; bytes.buffer may be a shared/larger buffer.
  const copy = bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
    ? bytes.buffer
    : bytes.slice().buffer;
  const digest = await crypto.subtle.digest('SHA-256', copy);
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i++) hex += view[i].toString(16).padStart(2, '0');
  return hex;
}

// ── text normalization & shingles ────────────────────────────────────────────

/** Lowercase, strip punctuation, collapse whitespace. */
export function normalizeText(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s.,/-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Build the token set a document's MinHash is computed over: word-trigram shingles
 * of the normalized text PLUS strongly-weighted tokens from the extracted Filing
 * fields. Blending the fields in means two docs with the same party/amount/date
 * still score high even when the text differs (or is absent, e.g. AI-only extract).
 */
export function buildContentTokens(input: {
  rawText?: string | null;
  partyName?: string | null;
  totalAmount?: number | string | null;
  dateOfDocument?: string | null;
  invoiceRef?: string | null;
  currency?: string | null;
}): string[] {
  const tokens = new Set<string>();

  // Field tokens — repeated with a prefix so they're distinct from text words and
  // carry weight even in a large text token set.
  const party = normalizeText(String(input.partyName || ''));
  if (party) party.split(' ').forEach(w => { if (w.length >= 2) tokens.add('p:' + w); });
  const amt = input.totalAmount == null || input.totalAmount === '' ? null : Number(input.totalAmount);
  if (amt != null && !isNaN(amt)) tokens.add('amt:' + amt.toFixed(2));
  const date = String(input.dateOfDocument || '').trim();
  if (date) tokens.add('date:' + date);
  const ref = normalizeText(String(input.invoiceRef || '')).replace(/\s+/g, '');
  if (ref) tokens.add('ref:' + ref);
  const cur = String(input.currency || '').trim().toLowerCase();
  if (cur) tokens.add('cur:' + cur);

  // Word-trigram shingles of the body text.
  const words = normalizeText(String(input.rawText || '')).split(' ').filter(Boolean);
  if (words.length >= 3) {
    for (let i = 0; i + 2 < words.length; i++) {
      tokens.add(words[i] + ' ' + words[i + 1] + ' ' + words[i + 2]);
    }
  } else {
    words.forEach(w => tokens.add(w));
  }

  return Array.from(tokens);
}

// ── MinHash ──────────────────────────────────────────────────────────────────

// Seeded 32-bit string hash (FNV-1a variant). Deterministic across runs/devices.
function hashStr(str: string, seed: number): number {
  let h = (2166136261 ^ seed) >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  // extra avalanche so different seeds spread well
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995) >>> 0; h ^= h >>> 15;
  return h >>> 0;
}

/** MinHash signature: for each permutation, the min hash over all tokens. */
export function computeMinHash(tokens: string[], size = MINHASH_SIZE): number[] {
  const sig = new Array<number>(size).fill(0xffffffff);
  if (tokens.length === 0) return [];
  for (const tok of tokens) {
    for (let k = 0; k < size; k++) {
      const h = hashStr(tok, k);
      if (h < sig[k]) sig[k] = h;
    }
  }
  return sig;
}

/** Compact serialization (base36, comma-separated). Empty signature -> ''. */
export function serializeMinHash(sig: number[]): string {
  if (!sig || sig.length === 0) return '';
  return sig.map(n => (n >>> 0).toString(36)).join(',');
}

export function parseMinHash(s: string | null | undefined): number[] | null {
  if (!s) return null;
  const parts = s.split(',');
  if (parts.length === 0) return null;
  const out: number[] = [];
  for (const p of parts) {
    const n = parseInt(p, 36);
    if (isNaN(n)) return null;
    out.push(n >>> 0);
  }
  return out.length ? out : null;
}

/** Estimated Jaccard similarity (0..1) from two equal-length signatures. */
export function minhashSimilarity(a: number[] | null, b: number[] | null): number {
  if (!a || !b || a.length === 0 || b.length === 0) return 0;
  const n = Math.min(a.length, b.length);
  if (n === 0) return 0;
  let same = 0;
  for (let i = 0; i < n; i++) if (a[i] === b[i]) same++;
  return same / n;
}

/** One-shot helper: tokens -> serialized signature. */
export function contentSignatureFromTokens(tokens: string[]): string {
  return serializeMinHash(computeMinHash(tokens));
}

// ── document-level comparison & clustering ────────────────────────────────────

/** The minimal shape duplicate detection needs off a dms_documents row. */
export interface DupCandidate {
  id: number | string;
  createdAt?: string | null;
  fileHash?: string | null;
  /** parsed MinHash signature (stored content_minhash, or computed from fields). */
  sig: number[] | null;
}

/** Match % (0..100) between two candidates. Exact file hash wins outright. */
export function matchPercent(a: DupCandidate, b: DupCandidate): number {
  if (a.fileHash && b.fileHash && a.fileHash === b.fileHash) return 100;
  const sim = minhashSimilarity(a.sig, b.sig);
  return Math.round(sim * 100);
}

export interface DupGroupInfo {
  /** stable group id (the original's id) */
  groupId: number | string;
  /** the earliest-uploaded document in the group */
  originalId: number | string;
  isOriginal: boolean;
  /** this doc's match % against the group's original (100 for the original itself) */
  matchPct: number;
}

/**
 * Cluster candidates into duplicate groups (single-link: connected if their match
 * % >= threshold). Returns a map keyed by candidate id for members of any group of
 * size >= 2. Singletons are omitted. The "original" of a group is the earliest
 * createdAt (ties broken by smallest id).
 */
export function computeDuplicateGroups(
  candidates: DupCandidate[],
  threshold = DUP_THRESHOLD,
): Map<number | string, DupGroupInfo> {
  const result = new Map<number | string, DupGroupInfo>();
  const n = candidates.length;
  if (n < 2) return result;

  // Union-find
  const parent = candidates.map((_, i) => i);
  const find = (x: number): number => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const union = (a: number, b: number) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

  // Cache the highest pairwise match so we can show a meaningful % even when the
  // group is linked transitively. We always recompute vs the chosen original below.
  for (let i = 0; i < n; i++) {
    const ci = candidates[i];
    if (!ci.fileHash && (!ci.sig || ci.sig.length === 0)) continue; // nothing to match on
    for (let j = i + 1; j < n; j++) {
      const cj = candidates[j];
      if (!cj.fileHash && (!cj.sig || cj.sig.length === 0)) continue;
      if (matchPercent(ci, cj) >= threshold) union(i, j);
    }
  }

  // Gather members per root
  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r)!.push(i);
  }

  const ts = (s?: string | null) => { if (!s) return 0; const t = Date.parse(s); return isNaN(t) ? 0 : t; };
  for (const members of Array.from(groups.values())) {
    if (members.length < 2) continue;
    // original = earliest createdAt, then smallest id
    let origIdx = members[0];
    for (const m of members) {
      const a = candidates[m], o = candidates[origIdx];
      const ta = ts(a.createdAt), to = ts(o.createdAt);
      if (ta < to || (ta === to && String(a.id) < String(o.id))) origIdx = m;
    }
    const original = candidates[origIdx];
    for (const m of members) {
      const c = candidates[m];
      const isOrig = m === origIdx;
      result.set(c.id, {
        groupId: original.id,
        originalId: original.id,
        isOriginal: isOrig,
        matchPct: isOrig ? 100 : matchPercent(c, original),
      });
    }
  }
  return result;
}
